// ---------------------------------------------------------------------------
// useFunnelCalculation Hook
// ---------------------------------------------------------------------------
// Encapsulates funnel calculation via POST /api/v11/discovery/funnel/calculate.
// Uses useMutation (not useQuery) because:
//   - POST request, not idempotent GET
//   - Triggered manually when layers change
//   - No caching needed (result depends on current layers)
// ---------------------------------------------------------------------------

import { useCallback, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { funnelService } from '@/services/v11/funnelService'
import type { FunnelLayerInput, FunnelCalculateData } from '@/services/v11/funnelService'
import type { FunnelLayer } from '@/components/v11/funnel/types'

// ---------------------------------------------------------------------------
// Layer conversion: FunnelLayer → FunnelLayerInput (strip UI-only fields)
// ---------------------------------------------------------------------------

function toLayerInputs(layers: readonly FunnelLayer[]): readonly FunnelLayerInput[] {
  return layers.map((layer) => ({
    type: layer.type,
    dimension: layer.dimension,
    values: [...layer.values],
  }))
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFunnelCalculation(includeOwnChannels: boolean = false) {
  const mutation = useMutation({
    mutationFn: async (layers: readonly FunnelLayer[]) => {
      const inputs = toLayerInputs(layers)
      const response = await funnelService.calculate(inputs, includeOwnChannels)
      return response.data
    },
  })

  // Use refs to avoid stale closures in useCallback
  const mutateRef = useRef(mutation.mutate)
  const resetRef = useRef(mutation.reset)
  mutateRef.current = mutation.mutate
  resetRef.current = mutation.reset

  const calculate = useCallback(
    (layers: readonly FunnelLayer[]) => {
      if (layers.length === 0) {
        resetRef.current()
        return
      }
      mutateRef.current(layers)
    },
    [],
  )

  return {
    results: mutation.data as FunnelCalculateData | undefined,
    isCalculating: mutation.isPending,
    error: mutation.error,
    calculate,
    reset: mutation.reset,
  }
}
